import { type UseQueryResult } from "@tanstack/react-query";

import {
  type CustomFieldRecord,
  TargetEntityType,
} from "@/shared/domain/customFields";
import { useReadyCustomFields } from "@/shared/domain/customFields/hooks";
import {
  useDatasetMetadataKeys,
  useDatasetTags,
} from "@/shared/domain/datasets/hooks";
import {
  useEventMetadataKeys,
  useEventNames,
  useEventTags,
} from "@/shared/domain/events/hooks";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import {
  useFileMetadataKeys,
  useOrgFileTags,
} from "@/shared/domain/files/hooks";
import { useAllTopicKeys } from "@/shared/domain/topics/hooks";

import { QueryTarget, queryTargetToEntityType } from "../QueryRecord";
import { type RoboqlExternalCtx } from "../RoboqlExternalCtx";

/**
 * The RoboQL editor's custom-field inputs for a target: the resolved
 * `release-custom-fields` flag and the target's Ready custom fields.
 *
 * `customFields` is populated ONLY for targets that support custom fields
 * (Datasets/Events, per {@link queryTargetToEntityType}) and is `undefined`
 * otherwise. That is a load-bearing contract: the completion provider gates the
 * `custom` namespace and the custom `SORT BY` keys on the presence of this
 * handle, so an unsupported target must never receive one. `useReadyCustomFields`
 * requires a concrete entity type and must run every render (rules of hooks), so
 * a `Dataset` placeholder is passed with `enabled: false` for unsupported targets
 * and its never-fetched result is discarded here.
 */
const useRoboqlCustomFields = (
  target: QueryTarget,
  orgId: string,
): {
  customFieldsEnabled: boolean;
  customFields?: UseQueryResult<CustomFieldRecord[], Error>;
} => {
  const customFieldsEnabled = useCheckFeatureFlag("release-custom-fields");
  const customFieldEntityType = queryTargetToEntityType(target);
  const targetSupportsCustomFields = customFieldEntityType !== undefined;
  const customFieldsQuery = useReadyCustomFields({
    entityType: customFieldEntityType ?? TargetEntityType.Dataset,
    orgId,
    enabled: targetSupportsCustomFields,
  });

  return {
    customFieldsEnabled,
    customFields: targetSupportsCustomFields ? customFieldsQuery : undefined,
  };
};

export const useRoboqlExternalCtx = (
  target: QueryTarget,
  orgId: string,
): RoboqlExternalCtx => {
  const fileTagsQuery = useOrgFileTags(orgId);
  const fileMetadataKeysQuery = useFileMetadataKeys(orgId);
  const datasetTagsQuery = useDatasetTags(orgId);
  const datasetMetadataKeysQuery = useDatasetMetadataKeys(orgId);
  const allTopicKeysQuery = useAllTopicKeys(orgId);
  const eventNamesQuery = useEventNames(orgId);
  const eventTagsQuery = useEventTags(orgId);
  const eventMetadataKeysQuery = useEventMetadataKeys(orgId);

  const { customFieldsEnabled, customFields } = useRoboqlCustomFields(
    target,
    orgId,
  );

  return {
    target,
    customFieldsEnabled,
    customFields,
    datasets: {
      tags: datasetTagsQuery,
      metadataKeys: datasetMetadataKeysQuery,
    },
    files: {
      tags: fileTagsQuery,
      metadataKeys: fileMetadataKeysQuery,
    },
    topics: allTopicKeysQuery,
    events: {
      names: eventNamesQuery,
      tags: eventTagsQuery,
      metadataKeys: eventMetadataKeysQuery,
    },
  };
};
